import { BaseErrorModel, SelectModel } from '@/models/core.model';
import React, { useEffect, useState } from 'react';
import { Loader, TagPicker } from 'rsuite';
import ErrorMessage from './ErrorMessage';
import { AxiosError } from 'axios';
import { toastErrorMessage, toastSuccessMessage } from '@/utils/toasterMessage';

interface AssignTagPickerProps {
  fetchData: (text?: string) => Promise<SelectModel[]>; // Function to fetch data
  fetchAssignedItems: () => Promise<number[]>; // Function to fetch data
  onAssign: (itemId: number) => Promise<any>;
  onUnAssign: (itemId: number) => Promise<any>;
  disabled?: boolean;
  block?: boolean
}

const AssignTagPicker: React.FC<AssignTagPickerProps> = ({
  fetchData,
  fetchAssignedItems,
  onAssign,
  onUnAssign,
  disabled,
  block,
}) => {

  const [data, setData] = useState<SelectModel[]>([]);
  const [assignedItems, setAssignedItems] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [fetchDataError, setFetchDataError] = useState<BaseErrorModel|undefined>(undefined);
  const [fetchAssignedError, setFetchAssignedError] = useState<BaseErrorModel|undefined>(undefined);
  
  // Fetch data when component mounts
  useEffect(() => {
    handleFetchData('');
    handleFetchAssignedItems();
  }, []);
  
  // Function to handle data fetching
  const handleFetchData = async (text?: string) => {
    setLoading(true);
    setFetchDataError(undefined);
    try {
      const result = await fetchData(text);
      setData(result);
    } catch (error) {
      setFetchDataError((error as AxiosError<BaseErrorModel>).response?.data);
    } finally {
      setLoading(false);
    }
  };
  
  // Function to handle assigned items fetching
  const handleFetchAssignedItems = async () => {
    setLoading(true);
    setFetchAssignedError(undefined);
    try {
      const result = await fetchAssignedItems();
      setAssignedItems(result);
    } catch (error) {
      setFetchAssignedError((error as AxiosError<BaseErrorModel>).response?.data);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSelect = (value, item) => {
    const isAdded = value.includes(item.value);
    if (isAdded) {
      handleAssign(item);
    } else {
      handleUAssign(item);
    }
  };
  
  const handleAssign = async (item: SelectModel) => {
    try {
      setAssignedItems(pre => ([...pre, item.value]));
      await onAssign(item.value);
      toastSuccessMessage(`${item.label} Successfully ASSIGNED.`);
    } catch (error) {
      setAssignedItems(pre => pre.filter(i => i !== item.value));
      const errorModel = (error as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage( `Error in ASSIGNING '${item.label}': ${errorModel?.errorMessage}`);
    } finally {
    }
  };
  const handleUAssign = async (item: SelectModel) => {
    try {
      setAssignedItems(pre => pre.filter(i => i !== item.value));
      await onUnAssign(item.value);
      toastSuccessMessage(`${item.label} Successfully UNASSIGNED.`);
    } catch (error) {
      setAssignedItems(pre => ([...pre, item.value]));
      const errorModel = (error as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage( `Error in UNASSIGNING '${item.label}': ${errorModel?.errorMessage}`);
    } finally {
    }
  };
  
  // Function to render menu and handle loading and error
  const renderMenu = (menu: any) => {
    const loadingMenu = () =>
    <>
      <div style={{ padding: 4, color: '#999', textAlign: 'center' }}>
        <Loader size="sm" /> Loading...
      </div>
    </>;
  
    const errorMenu = (error: BaseErrorModel|undefined, header: string) =>
    <>
      <div style={{ margin: 10 }}>
        <ErrorMessage model={error} header={header}></ErrorMessage>
      </div>
    </>;

    return(
      <>
        {menu}
        {loading && loadingMenu()}
        {fetchDataError && errorMenu(fetchDataError, 'Error in fetching data:')}
        {fetchAssignedError && errorMenu(fetchAssignedError, 'Error in fetching assigned items:')}
      </>
    );
  };
  
  return (
    <TagPicker
      block={block ?? true}
      disabled={disabled}
      loading={loading}
      cleanable={true}
      searchable
      tagProps={{ closable: false }}
      onSelect={handleSelect}
      data={data}
      value={assignedItems}
      onSearch={handleFetchData}
      onOpen={()=>handleFetchData}
      renderOption={renderMenu}
    />
  );
};

export default AssignTagPicker;